"use client"

import type React from "react"
import { useEffect, useState, useRef } from "react"
import { useCursorContext } from "./CursorProvider"
import { mouseStore } from "../mouseStore"

interface CursorProps {
  zIndex?: number
  showOnTouch?: boolean
  trailLength?: number
  /**
   * When true, the cursor squashes and stretches in the direction of travel
   * based on spring velocity. Applies only to circle shapes without a label.
   */
  elongate?: boolean
}

// Velocity Verlet spring integrator — stable at variable frame rates
function stepSpring(
  pos: number,
  vel: number,
  target: number,
  stiffness: number,
  damping: number,
  mass: number,
  dt: number
): [number, number] {
  const force = -stiffness * (pos - target) - damping * vel
  const acc = force / mass
  const newVel = vel + acc * dt
  const newPos = pos + vel * dt + 0.5 * acc * dt * dt
  return [newPos, newVel]
}

export const Cursor: React.FC<CursorProps> = ({ zIndex = 9999, showOnTouch = false, trailLength = 10, elongate = false }) => {
  const { cursorState } = useCursorContext()
  const [isVisible, setIsVisible] = useState(false)
  const [isTouchDevice, setIsTouchDevice] = useState(false)
  const [reducedMotion, setReducedMotion] = useState(false)

  const outerRef = useRef<HTMLDivElement>(null)
  const positionHistoryRef = useRef<{ x: number; y: number }[]>([])

  // All mutable animation state lives in refs — never causes re-renders
  const springRef = useRef({ x: 0, y: 0, vx: 0, vy: 0 })
  const targetRef = useRef({ x: 0, y: 0 })
  const rafRef = useRef<number | null>(null)
  const lastTimeRef = useRef<number | null>(null)
  const springConfigRef = useRef({ stiffness: 800, damping: 80, mass: 1 })
  const reducedMotionRef = useRef(false)
  const elongateRef = useRef(elongate)
  const lastAngleRef = useRef(0)
  const cursorShapeRef = useRef({ shape: cursorState.shape, label: cursorState.label })
  // Refs to inner element wrapper divs so RAF can update their transforms directly
  const innerRefsRef = useRef<(HTMLDivElement | null)[]>([])

  useEffect(() => {
    springConfigRef.current = {
      stiffness: cursorState.transition?.stiffness ?? 800,
      damping: cursorState.transition?.damping ?? 80,
      mass: cursorState.transition?.mass ?? 1,
    }
  }, [cursorState.transition])

  useEffect(() => { elongateRef.current = elongate }, [elongate])
  useEffect(() => {
    cursorShapeRef.current = { shape: cursorState.shape, label: cursorState.label }
  }, [cursorState.shape, cursorState.label])

  // Subscribe to shared mouse store — no duplicate window.mousemove listener.
  // targetRef is updated synchronously; the RAF loop reads it on the next frame.
  useEffect(() => {
    return mouseStore.subscribe((x, y) => {
      targetRef.current = { x, y }
      positionHistoryRef.current = [
        { x, y },
        ...positionHistoryRef.current.slice(0, trailLength - 1),
      ]
    })
  }, [trailLength])

  // Single RAF loop — starts once, reads only from refs, writes only to DOM.
  // Inner element parallax is also updated here to avoid React re-renders.
  useEffect(() => {
    const animate = (time: number) => {
      if (lastTimeRef.current !== null) {
        if (reducedMotionRef.current) {
          const { x, y } = targetRef.current
          springRef.current = { x, y, vx: 0, vy: 0 }
          if (outerRef.current) outerRef.current.style.transform = `translate(${x}px, ${y}px)`
        } else {
          const dt = Math.min((time - lastTimeRef.current) / 1000, 0.064)
          const { stiffness, damping, mass } = springConfigRef.current
          const { x, y, vx, vy } = springRef.current
          const { x: tx, y: ty } = targetRef.current

          // Sub-step to keep Velocity Verlet stable for stiff spring configs.
          // Stability requires dt < sqrt(2)/omega where omega = sqrt(k/m).
          // 8ms sub-steps are stable up to omega ~= 177 rad/s (k/m ~= 31,000).
          const MAX_SUB_DT = 0.008
          const steps = Math.max(1, Math.ceil(dt / MAX_SUB_DT))
          const subDt = dt / steps
          let nx = x, nvx = vx, ny = y, nvy = vy
          for (let i = 0; i < steps; i++) {
            ;[nx, nvx] = stepSpring(nx, nvx, tx, stiffness, damping, mass, subDt)
            ;[ny, nvy] = stepSpring(ny, nvy, ty, stiffness, damping, mass, subDt)
          }

          springRef.current = { x: nx, y: ny, vx: nvx, vy: nvy }

          if (outerRef.current) {
            const canElongate =
              elongateRef.current &&
              cursorShapeRef.current.shape !== "square" &&
              !cursorShapeRef.current.label

            if (canElongate) {
              const speed = Math.sqrt(nvx * nvx + nvy * nvy)
              const elong = Math.min(1 + speed / 800, 1.35)
              const comp = 1 / Math.sqrt(elong)
              if (speed > 30) lastAngleRef.current = Math.atan2(nvy, nvx) * (180 / Math.PI)
              outerRef.current.style.transform =
                `translate(${nx}px, ${ny}px) rotate(${lastAngleRef.current}deg) scaleX(${elong}) scaleY(${comp})`
            } else {
              outerRef.current.style.transform = `translate(${nx}px, ${ny}px)`
            }
          }

          // Update inner element parallax via direct DOM — zero React re-renders
          const innerDivs = innerRefsRef.current
          if (innerDivs.length > 0) {
            const cur = targetRef.current
            for (let i = 0; i < innerDivs.length; i++) {
              const el = innerDivs[i]
              if (!el) continue
              const delayed = positionHistoryRef.current[Math.min(i + 1, positionHistoryRef.current.length - 1)] || cur
              const ox = -(cur.x - delayed.x) * 0.5
              const oy = -(cur.y - delayed.y) * 0.5
              el.style.transform = `translate(calc(-50% + ${ox}px), calc(-50% + ${oy}px))`
            }
          }
        }
      }
      lastTimeRef.current = time
      rafRef.current = requestAnimationFrame(animate)
    }

    rafRef.current = requestAnimationFrame(animate)
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current)
    }
  }, [])

  // Device + accessibility detection
  useEffect(() => {
    const touchMQ = window.matchMedia("(pointer: coarse)")
    setIsTouchDevice(touchMQ.matches)

    const motionMQ = window.matchMedia("(prefers-reduced-motion: reduce)")
    reducedMotionRef.current = motionMQ.matches
    setReducedMotion(motionMQ.matches)

    const onMotionChange = (e: MediaQueryListEvent) => {
      reducedMotionRef.current = e.matches
      setReducedMotion(e.matches)
    }
    motionMQ.addEventListener("change", onMotionChange)

    const timer = setTimeout(() => setIsVisible(true), 50)

    return () => {
      motionMQ.removeEventListener("change", onMotionChange)
      clearTimeout(timer)
    }
  }, [])

  if (isTouchDevice && !showOnTouch) return null

  const {
    width = 24,
    height = 24,
    scale = 1,
    shape = "circle",
    color = "rgba(255, 255, 255, 0.8)",
    opacity = 1,
    borderColor = "rgba(0, 0, 0, 0)",
    borderWidth = 0,
    borderStyle = "solid",
    mixBlendMode = "normal",
    label,
    fontSize = "12px",
    fontWeight = "bold",
    labelColor = "#000",
    customElement,
    innerElements,
    borderRadius: customBorderRadius,
  } = cursorState

  const borderRadius =
    shape === "circle" ? "50%" : shape === "square" ? `${customBorderRadius ?? 0}px` : "0"

  const bgColor = shape === "custom" ? "transparent" : color

  const shapeTransition = reducedMotion
    ? "none"
    : "width 0.2s ease, height 0.2s ease, border-radius 0.2s ease, background-color 0.2s ease, border-color 0.2s ease, opacity 0.2s ease, transform 0.15s ease"

  // Keep the inner refs array in sync with the count of inner elements
  if (innerElements) {
    innerRefsRef.current = innerRefsRef.current.slice(0, innerElements.length)
  } else {
    innerRefsRef.current = []
  }

  return (
    <div
      ref={outerRef}
      aria-hidden="true"
      style={{
        position: "fixed",
        left: 0,
        top: 0,
        zIndex,
        pointerEvents: "none",
        opacity: isVisible ? 1 : 0,
        willChange: "transform",
        mixBlendMode,
        transformOrigin: "0 0",
      }}
    >
      <div
        style={{
          width,
          height,
          borderRadius,
          backgroundColor: bgColor,
          opacity,
          borderColor,
          borderWidth,
          borderStyle,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          transform: `translate(-50%, -50%) scale(${scale})`,
          transition: shapeTransition,
          willChange: "width, height, background-color, transform",
        }}
      >
        {shape === "custom" && customElement}

        {label && (
          <div
            style={{
              fontSize,
              fontWeight,
              color: labelColor,
              textAlign: "center",
              userSelect: "none",
              whiteSpace: "nowrap",
              pointerEvents: "none",
            }}
          >
            {label}
          </div>
        )}

        {innerElements?.map((item, index) => (
          <div
            key={`inner-${index}`}
            ref={(el) => { innerRefsRef.current[index] = el }}
            style={{
              position: "absolute",
              left: "50%",
              top: "50%",
              // Initial transform — RAF overwrites it every frame via direct DOM
              transform: "translate(-50%, -50%)",
              transition: reducedMotion ? "none" : "transform 0.12s ease",
            }}
          >
            {item.element}
          </div>
        ))}
      </div>
    </div>
  )
}
